<template>
  <div>
    <div class="checkin-header">
      <h1>
        출석체크를 위해 <br>
        회원 번호를 입력해주세요.
      </h1>
      <div class="checkin-description">
        전화번호 뒤 4자리
      </div>
    </div>

    <input
      ref="inputMemberCode"
      v-model="memberCode"
      class="checkin__input-member-id"
      type="text"
      maxlength="4"
      @keypress.enter="handleClickConfirm"
    >

    <div class="checkin__keypad">
      <button
        v-for="num in 9"
        class="press-animation"
        @click="handleTouchButton(num.toString())"
      >
        {{ num }}
      </button>
      <button
        class="delete press-animation"
        @click="deleteLetter"
      >
        수정
      </button>
      <button
        class="press-animation"
        @click="handleTouchButton('0')"
      >
        0
      </button>
      <button
        v-loading="isConfirmLoading"
        class="confirm press-animation"
        :class="{
          disabled: isConfirmDisabled
        }"
        @click="handleClickConfirm"
      >
        확인
      </button>
    </div>

    <div class="footer">
      © FIFL Inc.
    </div>

    <self-checkin-dialog 
      :is-form-dialog-visible="isMemberSelectDialogVisible"
      :title="&quot;중복된 회원 번호가 있습니다.&quot;"
      :description="&quot;본인 확인 후 출석체크를 진행할 계정을 선택해주세요.&quot;"
      @close="handleDialogClose(&quot;member&quot;)"
    >
      <self-checkin-dialog-item
        v-for="member in members"
        :key="member.id"
        @click="checkMemberCode(member)"
      >
        <span>{{ member.member.name }}</span>
        <div class="vr">
          |
        </div>
        <span>{{ presentPhoneNum(member.member.phone_number) }}</span>
        <div>
          <span>{{ member.member.email }}</span>
        </div>
      </self-checkin-dialog-item>
    </self-checkin-dialog>

    <self-checkin-dialog 
      :is-form-dialog-visible="isMembershipSelectDialogVisible"
      :title="&quot;이용 중인 회원권이 여러 개로 확인됩니다.&quot;"
      :description="&quot;출석체크하실 회원권을 하나 선택해 주세요.&quot;"
      @close="handleDialogClose(&quot;membership&quot;)"
    >
      <self-checkin-dialog-item
        v-for="membership in memberships"
        :key="membership.id"
        @click="handleSelectMembership(membership)"
      >
        <span class="dialog-item__name">
          {{ membership.parent_membership.name }}
        </span>
        <div class="dialog-item__contents">
          <span>{{ `${membership.start} - ${membership.end}` }}</span>
          <span>
            {{ `(잔여 ${membershipSystem.middle} : ${membership.balance === -1 ? '무제한' : membership.balance - membership.consumed})` }}
          </span>
        </div>
      </self-checkin-dialog-item>
    </self-checkin-dialog>

    <audio ref="elErrorAudio">
      <source
        src="//cdn.fiflbox.com/admin/checkin/fiflbox_error.wav"
        type="audio/wav"
      >
    </audio>
  </div>
</template>

<script>
import SelfCheckinDialog from './SelfCheckinDialog.vue';
import SelfCheckinDialogItem from './SelfCheckinDialogItem.vue';
import { computedGetters } from '@/utils/vuex-helper';

const ALERT_MESSAGES = {
  noMember: {
    title: '회원 번호 오류',
    message: '존재하지 않은 회원입니다. 정확한 회원 번호를 입력해주세요.'
  },
  unknownError: {
    title: '출석체크 불가',
    message:
      '알 수 없는 오류가 발생했습니다. 인터넷 연결 상태를 확인해주시거나 앱을 재시작해주세요.'
  },
  noAvailableMembership: {
    title: '출석체크 불가',
    message:
      '현재 이용 가능한 회원권이 없습니다. 재등록 후 이용해주시길 바립니다.'
  }
};

export default {
  name: 'MemberCheck',
  components: {
    SelfCheckinDialog,
    SelfCheckinDialogItem
  },
  data() {
    return {
      memberCode: '',
      isConfirmLoading: false,
      isCheckMemberLoading: false,
      isMemberSelectDialogVisible: false,
      isMembershipSelectDialogVisible: false,
      members: [],
      memberships: []
    };
  },
  computed: {
    ...computedGetters(['membershipSystem', 'isLightGreyPlan']),
    isConfirmDisabled() {
      return this.memberCode.length !== 4;
    }
  },
  mounted() {
    // 이전 데이터 삭제
    this.clearData();
    this.$refs.inputMemberCode.focus();
  },
  methods: {
    clearData() {
      this.memberCode = '';
      this.$store.commit('resetCheckInData');
    },
    deleteLetter() {
      this.memberCode = this.memberCode.slice(0, -1);
    },
    async alert({ title, message }) {
      await this.$alert(message, title, {
        confirmButtonText: '확인',
        showClose: false,
        customClass: 'fifl-message-box',
        confirmButtonClass: 'gray',
        cancelButtonClass: 'white'
      });
    },
    async handleClickConfirm() {
      await this.$store.dispatch('syncCenterAccount');

      if (this.isLightGreyPlan) {
        return this.$router.replace('/announce/access-block').catch(() => {});
      }

      if (this.isConfirmDisabled) {
        return;
      }

      if (this.isConfirmLoading) {
        return;
      }

      this.isConfirmLoading = true;

      try {
        await this.syncMembersByCode();
      } catch (err) {
        console.log(err);
      }

      this.isConfirmLoading = false;
    },
    async syncMembersByCode() {
      try {
        this.members = await this.$store.dispatch(
          'getMembersByCode',
          this.memberCode
        );
      } catch (err) {
        return this.alert(ALERT_MESSAGES.unknownError);
      }

      if (this.members.length === 0) {
        this.clearData();
        this.$refs.elErrorAudio.play();
        return this.alert(ALERT_MESSAGES.noMember);
      }

      if (this.members.length > 1) {
        return (this.isMemberSelectDialogVisible = true);
      }

      this.checkMemberCode(this.members[0]);
    },
    async checkMemberCode(member) {
      if (this.isCheckMemberLoading) {
        return;
      }

      this.isCheckMemberLoading = true;

      try {
        await this.validateMember(member);
      } catch (err) {
        console.log(err);
      }

      this.isCheckMemberLoading = false;
    },
    async handleSelectMembership(membership) {
      await this.setCurrentMembership(membership);
      this.isMembershipSelectDialogVisible = false;
      this.$router.push('/checkin/course').catch(() => {});
    },
    async validateMember(member) {
      try {
        this.memberships = await this.$store.dispatch('getMemberships', member);
      } catch (err) {
        console.log(err);
      }

      if (!this.memberships || this.memberships.length === 0) {
        this.clearData();
        this.$refs.elErrorAudio.play();
        return this.alert(ALERT_MESSAGES.noAvailableMembership);
      }

      this.$store.commit('setCheckedMember', member);
      // 1. 멤버십 하나일 때
      if (this.memberships.length === 1) {
        const currentChildMembership = this.memberships[0];
        this.setCurrentMembership(currentChildMembership);
        this.$router.push('/checkin/course').catch(() => {});
      }
      // 2. 멤버십 여러개 일 때,
      else if (this.memberships.length > 1) {
        this.isMemberSelectDialogVisible = false;
        setTimeout(() => {
          this.isMembershipSelectDialogVisible = true;
        }, 500);
        return;
      }
      // 작동하지않는 로직 (아마도..)
      else {
        this.clearData();
        this.$refs.elErrorAudio.play();
        return this.alert(ALERT_MESSAGES.noAvailableMembership);
      }
    },
    presentPhoneNum(phoneNum) {
      return phoneNum.replace(
        /(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/,
        '$1-$2-$3'
      );
    },
    handleTouchButton(value) {
      if (this.memberCode.length >= 4) {
        return;
      }

      this.memberCode += value;
    },
    handleDialogClose(type) {
      switch (type) {
        case 'member':
          return (this.isMemberSelectDialogVisible = false);
        case 'membership':
          return (this.isMembershipSelectDialogVisible = false);
      }
    },
    async setCurrentMembership(membership) {
      this.$store.commit('setChildMembership', membership);
      await this.$store.dispatch('syncAttendableCourses', {
        childMembershipId: membership.id
      });
    }
  }
};
</script>

<style lang="scss">
.checkin__input-member-id {
  border-radius: 0;
  font-size: 33px;
  line-height: 1;
  color: #f2f2f2;

  border: 0;
  background-color: transparent;
  border-bottom: solid 1px #d3d3d3;
  text-align: center;
  padding-bottom: 12px;
  width: 300px;
  margin: 0 auto;
  display: block;

  &:focus {
    outline: 0;
  }

  @media screen and (max-height: 1023px) {
    /**
       * Small Desktop or Tablet (768px < height < 1024px)
       */
    width: 210px;
    font-size: 27px;
  }

  @media screen and (max-height: 700px) {
    /**
       * Mobile (height < 768px)
       */

    width: 200px;
    font-size: 20px;
  }
}

.checkin__keypad {
  width: 360px;
  margin: 30px auto 0;

  button {
    width: 105px;
    height: 105px;
    border-radius: 13px;
    background-color: #ffffff;
    border: 0;
    font-size: 35px;
    text-align: center;
    line-height: normal;
    vertical-align: middle;
    font-weight: normal;
    margin: 7px;

    &.delete {
      font-size: 25px;
    }

    &.confirm {
      font-size: 25px;
      color: #ffffff;
      background-color: #b9b9b9;

      .el-loading-mask {
        border-radius: 13px;
        background-color: rgba(255, 255, 255, 0.21);
      }

      &.disabled {
        color: #b9b9b9;
        background-color: #d3d3d3;
        cursor: not-allowed;

        &:active {
          background-color: #d3d3d3;
          box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
        }
      }
    }
  }

  @media screen and (max-height: 1023px) {
    /**
       * Small Desktop or Tablet (768px < height < 1024px)
       */
    width: 240px;

    button {
      width: 70px;
      height: 70px;
      border-radius: 7px;
      margin: 5px;
      font-size: 25px;

      &.delete {
        font-size: 18px;
      }

      &.confirm {
        font-size: 18px;

        .el-loading-mask {
          border-radius: 7px;
        }
      }
    }
  }

  @media screen and (max-height: 700px) {
    /**
       * Mobile (height < 768px)
       */
    margin: 10px auto 0;
  }
}

@media screen and (max-width: 768px) {
  .checkin__input-member-id {
    width: 195px;
    line-height: 29px;
    font-size: 20px;
    margin: 0 auto;
  }

  .checkin__keypad {
    width: 240px;

    button {
      font-size: 25px;
      width: 70px;
      height: 70px;
      border-radius: 7px;
      line-height: 70px;
      margin: 5px;

      &.modify {
        font-size: 18px;
      }

      &.confirm {
        font-size: 18px;
        color: #ffffff;
        background-color: #b9b9b9;
      }
    }
  }
}
</style>
